import { Component, input, model, Optional, Self, SimpleChanges, effect, OnChanges, computed } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { DropdownData } from '../../models/dropdown.models';
import { MultiSelectModule } from '@openng/optimus-ui/multiselect';
import { SelectModule } from '@openng/optimus-ui/select';
import { ChipModule } from '@openng/optimus-ui/chip';

// export const OPTION_ALL: DropdownData = {
//   id: '',
//   firstLabel: 'All'
// };

@Component({
  selector: 'generic-dropdown',
  imports: [FormsModule, NgSelectModule, MultiSelectModule, SelectModule, ChipModule],
  templateUrl: './generic-dropdown.component.html',
  styleUrls: ['./generic-dropdown.component.scss']
})
export class GenericDropdownComponent implements ControlValueAccessor, OnChanges {
  // Signal-based inputs
  items = model<DropdownData[] | null>([]);
  label = input<string>(''); // class
  placeholder = input<string>('Select'); // class
  errormessage = input<string>(''); // class
  required = input<boolean>(false); //class
  allowSearch = input<boolean>(true);
  maxLabels = input<number>(3);
  clearable = input<boolean>(false);
  multiselect = input<boolean>(false);
  selectFirstOption = input<boolean>(false);
  disabled = input<boolean>(false);
  loading = input<boolean>(false);
  virtualScroll = input<boolean>(false);
  virtualScrollItemSize = input<number>(0);
  optionAll = input<boolean>(false);
  optionAllLabel = input<string>('All');

  // Two-way binding with model
  value = model<string>();

  searchFn = (term: string, item: DropdownData) => {
    term = term.toLowerCase();
    return item.firstLabel?.toLowerCase().includes(term) || item.secondLabel?.toLowerCase().includes(term);
  };
  constructor(@Optional() @Self() public ngControl: NgControl | null = null) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    // React to disabled input changes by enabling/disabling control
    effect(() => {
      const isDisabled = this.disabled();
      const control = this.ngControl?.control;
      if (!control) return;
      if (isDisabled && !control.disabled) {
        control.disable({ emitEvent: false });
      } else if (!isDisabled && control.disabled) {
        control.enable({ emitEvent: false });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] && this.selectFirstOption()) {
      this.value.set(this.items()?.[0]?.id ?? '00000000-0000-0000-0000-000000000000');
    }
  }

  displayItems = computed(() => {
    const items = this.items() ?? [];
    return this.optionAll()
      ? [{ id: '00000000-0000-0000-0000-000000000000', firstLabel: this.optionAllLabel() }, ...items]
      : items;
  });
  get hasError(): boolean {
    return !!(this.ngControl?.control?.errors && (this.ngControl.control.touched || this.ngControl.control.dirty));
  }

  getTypedItems(items: unknown[]): DropdownData[] {
    return items as DropdownData[];
  }
  protected onTouched: () => void = () => {};
  protected onChange: (value: string) => void = () => {};

  writeValue(value: string): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    const control = this.ngControl?.control;
    if (!control) return;
    if (isDisabled && !control.disabled) {
      control.disable({ emitEvent: false });
    } else if (!isDisabled && control.disabled) {
      control.enable({ emitEvent: false });
    }
  }

  onValueChange(value: string): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }
}
